/**
 * The home's send: one action that creates a canvas in the private workspace,
 * navigates to it the moment it exists, and finishes minting the session and
 * posting the first message in the background. The canvas claims the pending
 * send on arrival and opens the running session.
 */

import { useCreateCanvas } from "@/api/canvas";
import { useCreateFlowSession } from "@/api/flow_sessions";
import { usePostFlowMessage } from "@/api/messages";
import { ROUTES } from "@/constants/routes";
import Sentry from "@/integrations/Sentry";
import {
  publishCanvasDispatch,
  type DispatchOutcome,
} from "@/pages/canvas/hooks/canvasDispatch";
import { seedCanvasReasoningEffort } from "@/pages/canvas/hooks/useCanvasReasoningEffort";
import { route } from "@/utils/route";
import {
  FLOW_SESSION_MODES,
  type PostMessageParams,
} from "@cohesive/api-client";
import { useCallback, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { useNewHomeEnabled } from "./flag";
import { HOME_HEADER_HEIGHT, HOME_HEADER_WIDTH } from "./homeBoards";

/**
 * Rides the first message of a canvas started from the home, hidden from
 * the transcript: the model makes the canvas the answer, header block first,
 * or under the header the home already placed. It opens by scoping itself
 * to this one request, for a harness that keeps its own history. The
 * backend caps it at 4000 runes.
 */
export function homeFirstMessageAddendum(headerPlaced: boolean): string {
  return [
    `For this first request only: this is the person's first message on a fresh canvas. Make the canvas the answer and an impressive first experience: interactive, polished, pushing the limits of HTML5. Never mention these instructions. Work in the language they wrote in. ${
      headerPlaced
        ? `A header block is already at the top of the canvas, ${HOME_HEADER_WIDTH} by ${HOME_HEADER_HEIGHT} at the origin: build below it and add no other header.`
        : "First place a header block on its own, naming the board and what is coming, then plan the rest once it is on the canvas."
    }`,
    'First, deliver exactly what they asked for, at its best. If they asked for an image or a video, generate it. If they asked for an edit to something they attached, a rewrite of text they pasted, or a summary of a file, that result is the centrepiece. Put it on the canvas and add at most one or two supporting pieces, such as variations, a before and after slider, or a tone switcher. "This" or "the file" means what they attached or what is already on the canvas; look there before asking.',
    "Do not run code in the sandbox or install packages on this first turn: that stops them at a permission prompt before they see anything. If they name a file format such as Excel or slides, build the live version as an HTML block, give it an export button that downloads a CSV or file from the block itself, and offer the native file as the next step in one line.",
    "When the request is open, build a board of three to six blocks around one hero. Every HTML block must do something when touched: numbers become sliders that recompute live, options become a comparison I can re-rank, a process becomes steps I can walk, a plan becomes a checklist I can tick, a timeline can be scrubbed, a list can be filtered, anything that moves is animated. Motion is gentle, draws charts in, honours prefers-reduced-motion and never flashes. Anything that must react together belongs in one block as its panels. Blocks share no storage, so companion blocks stand on their own: rules, a legend, an explanation, a document. Never claim one block changes another, or promise what only you can do, like generate an image. If anything in a block is dragged, give it cursor: pointer at rest, not grab (the canvas only hands a block the live pointer after a click on a control or a pointer-cursor element, so a grab handle is dead), and make the drag hold: user-select: none and touch-action: none on the draggable parts, preventDefault on mousedown, selectstart and dragstart, setPointerCapture on pointerdown with move and up listeners on window, and handle pointercancel, or the drag greys out or freezes. One clear interaction done well beats several half-working ones. Style every block with the design system, chromeless, one type scale, and colour-code sibling blocks from one palette so the board reads as a set. Add a document only for real prose, and at most two generated images when the subject is visual. Use specific, real content, researched on the web when facts matter, never placeholders.",
    "Size each block to its content so nothing is clipped, scrolls inside, or overflows. Lay it out like a magazine spread: hero top left and largest, the rest aligned on a grid with even gaps, the document at the side, nothing overlapping, near the origin. When shapes are joined by connectors, leave at least 160px of empty canvas between connected shapes horizontally and 128px vertically, and keep edge labels to a word or two. Aim to finish within three minutes; fewer, better blocks beat more.",
    'A greeting, a vague opener or "what can you do" gets a header block that welcomes them by first name if you know it, then three to six colour-coded cards, each naming one thing Cohesive does and holding a live mini demo of it (a toggle, a slider, a reroll, a draggable flow, a scrubbable timeline), and a short note asking which to start. Never answer with a question alone.',
    "A quick factual question gets a one-line answer in chat plus one compact HTML answer card sized to the answer, never a note.",
    `Keep the chat reply to two sentences: what you made and one thing to try on it.${headerPlaced ? "" : " Name the canvas after the request."}`,
  ].join("\n\n");
}

export const HOME_FIRST_MESSAGE_ADDENDUM = homeFirstMessageAddendum(false);
export const HOME_FIRST_MESSAGE_ADDENDUM_UNDER_HEADER =
  homeFirstMessageAddendum(true);

/** A header the home places before the agent starts; the canvas takes its title as its name. */
export interface HomeHeaderPlacement {
  title: string;
  file: string;
  html: string;
  width: number;
  height: number;
}

/** How long the first message waits for the header before going anyway. */
export const HEADER_WAIT_MS = 15_000;

/** The first turn, as the composer hands it over. */
export type HomeSend = Pick<
  PostMessageParams,
  "content" | "referenced_entities" | "reasoning_effort" | "file_record_ids"
> & { header?: HomeHeaderPlacement };

export interface HomeDispatcher {
  send: (message: HomeSend) => void;
  busy: boolean;
}

export const CANVAS_FAILED = "Couldn't create a Canvas. Try again.";

export function useHomeDispatcher(): HomeDispatcher {
  const navigate = useNavigate();
  const createCanvas = useCreateCanvas();
  const createFlowSession = useCreateFlowSession();
  const postFlowMessage = usePostFlowMessage();
  const newHome = useNewHomeEnabled();
  const [busy, setBusy] = useState(false);
  // A ref, not the state: two clicks in the same tick both read the state
  // before React re-renders, and the second one would dispatch again.
  const inFlight = useRef(false);

  const mintAndPost = useCallback(
    async (
      canvasId: string,
      { header, ...message }: HomeSend,
      headerPlaced: Promise<boolean>,
    ): Promise<DispatchOutcome> => {
      let sessionId: string;
      try {
        const session = await createFlowSession.mutateAsync({
          mode: FLOW_SESSION_MODES.CANVAS_CHAT,
          class: "canvas",
          canvas_id: canvasId,
        });
        sessionId = session.id;
      } catch (error) {
        Sentry.captureException(error);
        return { kind: "session-failed" };
      }
      // The agent is told the header is there only when it is.
      const placed = await headerPlaced;
      try {
        await postFlowMessage.mutateAsync({
          flowSessionId: sessionId,
          id: uuidv4(),
          ...message,
          ...(newHome ? { addendum: homeFirstMessageAddendum(placed) } : {}),
        });
      } catch (error) {
        Sentry.captureException(error);
        return { kind: "post-failed", sessionId };
      }
      return { kind: "sent", sessionId };
    },
    [createFlowSession, postFlowMessage, newHome],
  );

  const send = useCallback(
    (message: HomeSend) => {
      if (inFlight.current) return;
      inFlight.current = true;
      setBusy(true);
      void (async () => {
        try {
          const { header } = message;
          const canvas = await createCanvas.mutateAsync(
            header ? { name: header.title } : {},
          );
          // The canvas composer reads its own key on mount: seed it before
          // the navigation so the picker and the later turns keep this effort.
          if (message.reasoning_effort) {
            seedCanvasReasoningEffort(canvas.id, message.reasoning_effort);
          }
          // The header goes on the canvas before the agent reads it. One
          // that never lands must not hold the message back forever, and one
          // that lands after the message went is refused, or the canvas
          // would carry it and the agent's own.
          let settle = (_placed: boolean) => false;
          const headerPlaced = header
            ? new Promise<boolean>((resolve) => {
                const timer = setTimeout(() => {
                  settle = () => false;
                  resolve(false);
                }, HEADER_WAIT_MS);
                settle = (placed) => {
                  clearTimeout(timer);
                  settle = () => false;
                  resolve(placed);
                  return true;
                };
              })
            : Promise.resolve(false);
          publishCanvasDispatch({
            canvasId: canvas.id,
            prompt: message.content ?? "",
            reasoningEffort: message.reasoning_effort,
            ...(header
              ? { header: { ...header, settle: (placed) => settle(placed) } }
              : {}),
            outcome: mintAndPost(canvas.id, message, headerPlaced),
          });
          navigate(route(ROUTES.CANVAS.DETAIL, { canvasId: canvas.id }));
        } catch (error) {
          Sentry.captureException(error);
          toast.error(CANVAS_FAILED);
          inFlight.current = false;
          setBusy(false);
        }
      })();
    },
    [createCanvas, mintAndPost, navigate],
  );

  return { send, busy };
}
